if ( typeof ShippingNotice !== 'function' ) {

  /* Survives the innerHTML swaps that re-create this element on every cart
     mutation, so the bar can animate from its previous fill instead of 0. */
  let lastShippingProgress = null;
  /* Same idea for the celebration: the confetti should fire on the mutation
     that crosses the threshold, not on every re-render while already over it
     (and not on a page load that starts over it). null means "first render". */
  let lastShippingSuccess = null;

  const CART_ICON = `<svg class="shipping-progress__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M1.5 2.5h3l2.4 11.4a1.7 1.7 0 0 0 1.7 1.3h8.6a1.7 1.7 0 0 0 1.7-1.3L21.5 6H5.3"/></svg>`;
  const UNLOCKED_ICON = `<svg class="shipping-progress__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M7.5 12.5l3 3 6-6.5"/></svg>`;

  const CONFETTI_COLORS = [
    'var(--color-kuli-kuli-neon-green, #52B480)',
    'var(--color-kuli-kuli-cream, #F4F5EA)',
    'var(--color-kuli-kuli-orange, #F15F23)'
  ];
  /* Scattered on purpose: the array index also sets each piece's release
     delay, so a sorted list would read as a left-to-right sweep instead of
     a burst. */
  const CONFETTI_LEFT = [8, 46, 72, 22, 90, 58, 34, 80, 14, 64, 40, 95, 28, 52, 76, 4, 68, 44];
  /* Last piece leaves at ~1s and lands at ~3.2s — long enough to register
     without holding up someone who is trying to check out. */
  const CONFETTI_STAGGER = 58;
  const CONFETTI_DURATION = 2200;

  function confettiPieces() {
    return CONFETTI_LEFT.map((left, index) => {
      const style = [
        `background:${CONFETTI_COLORS[index % CONFETTI_COLORS.length]}`,
        `left:${left}%`,
        `top:${3 + (index % 5)}px`,
        /* Sideways flutter and spin, alternated so no two neighbours fall alike. */
        `--confetti-drift:${(index % 2 ? -1 : 1) * (4 + (index % 5))}px`,
        `--confetti-spin:${(index % 3 ? 1 : -1) * (180 + (index % 4) * 30)}deg`,
        `animation-delay:${index * CONFETTI_STAGGER}ms`,
        `animation-duration:${CONFETTI_DURATION + (index % 4) * 90}ms`
      ].join('; ');
      return `<i style="${style}"></i>`;
    }).join('');
  }

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
      this.init();
    }

    init() {

      const freeShippingThreshold = Number(this.getAttribute('data-free-shipping'));
      const cartTotal = Number(this.getAttribute('data-cart-total-amount'));
      const threshold = Math.round(freeShippingThreshold * (Shopify.currency.rate ? Number(Shopify.currency.rate) : 1));
      /* Set by Liquid when every line in the cart is a subscription: those ship
         free on their own, so there is no remaining amount to make up. */
      const alreadyShipsFree = this.hasAttribute('data-free-shipping-met');
      const freeShippingRemaining = alreadyShipsFree ? 0 : cartTotal - threshold;
      const percent = ( alreadyShipsFree || threshold <= 0 ) ? 100 : Math.min(100, Math.round(cartTotal / threshold * 100));
      const itemCount = Number(this.getAttribute('data-item-count')) || 0;
      let shippingMessage = '';
      let shippingSuccess = false

      if ( freeShippingRemaining < 0 ) {
        shippingMessage = window.KROWN.settings.locales.shipping_notice_remaining_to_free.replace('{{ remaining_amount }}', this._formatMoney(Math.abs(freeShippingRemaining), KROWN.settings.shop_money_format));

      } else {
        /* Falls back to the old flat string if a stale layout hasn't been
           updated to pass the celebratory copy through KROWN. */
        shippingMessage = window.KROWN.settings.locales.shipping_notice_unlocked_free || window.KROWN.settings.locales.shipping_notice_eligible_for_free;
        shippingSuccess = true;
      }

      /* Only celebrate on the render that crosses the line. */
      const celebrate = shippingSuccess && lastShippingSuccess === false;

      const badgeMarkup = ( !shippingSuccess && itemCount > 0 )
        ? `<span class="shipping-progress__count" aria-hidden="true">${itemCount}</span>`
        : '';

      const confettiMarkup = celebrate
        ? `<div class="shipping-progress__confetti" aria-hidden="true">${confettiPieces()}</div>`
        : '';

      const progressMarkup = `
        <div class="shipping-progress ${shippingSuccess ? 'shipping-progress--complete' : ''}">
          ${confettiMarkup}
          <div class="shipping-progress__row">
            <span class="shipping-progress__icon">
              ${shippingSuccess ? UNLOCKED_ICON : CART_ICON}
              ${badgeMarkup}
            </span>
            <div class="shipping-progress__content">
              <p class="shipping-progress__caption">${shippingMessage}</p>
              <div class="shipping-progress__bar" aria-hidden="true">
                <span class="shipping-progress__fill" style="width: ${lastShippingProgress === null ? 0 : lastShippingProgress}%"></span>
              </div>
            </div>
          </div>
        </div>`;

      if ( this.hasAttribute('data-show-alert') ) {
        this.innerHTML = `
          <div class="alert alert--wide alert--${shippingSuccess ? 'success' : 'note'}">
            ${progressMarkup}
          </div>`;
      } else {
        this.innerHTML = progressMarkup;
      }

      const fill = this.querySelector('.shipping-progress__fill');
      if ( fill ) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.width = percent + '%';
          });
        });
      }
      lastShippingProgress = percent;
      lastShippingSuccess = shippingSuccess;

    }
    
    _formatMoney(cents, format) {

			if (typeof cents === 'string') {
				cents = cents.replace('.', '');
			}
	
			let value = '';
			const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
			const formatString = format || moneyFormat;
	
			function formatWithDelimiters(number, precision, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';
	
				if (isNaN(number) || number === null) {
					return 0;
				}
	
				number = (number / 100.0).toFixed(precision);
	
				const parts = number.split('.');
				const dollarsAmount = parts[0].replace(
					/(\d)(?=(\d\d\d)+(?!\d))/g,
					'$1' + thousands
				);
				const centsAmount = parts[1] ? decimal + parts[1] : '';
	
				return dollarsAmount + centsAmount;
			}
	
			switch (formatString.match(placeholderRegex)[1]) {
				case 'amount':
					value = formatWithDelimiters(cents, 2);
					break;
				case 'amount_no_decimals':
					value = formatWithDelimiters(cents, 0);
					break;
				case 'amount_with_comma_separator':
					value = formatWithDelimiters(cents, 2, '.', ',');
					break;
				case 'amount_no_decimals_with_comma_separator':
					value = formatWithDelimiters(cents, 0, '.', ',');
					break;
				case 'amount_no_decimals_with_space_separator':
					value = formatWithDelimiters(cents, 0, ' ');
					break;
				case 'amount_with_apostrophe_separator':
					value = formatWithDelimiters(cents, 2, "'");
					break;
			}
	
			return formatString.replace(placeholderRegex, value);
	
		}

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}